import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {AgencyApiService} from './api/agency-api.service';
import {AgencySubscriptions, AgencySubscriptionStatus, AgencySubscriptionType} from '../types/agency';
import {MCActionsService} from './mc-base/mc-actions-service';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {AgencyState, IProductsAgencyConfig, ProductsAgency, ProductsDetailsAgency} from '../types/product.type';
import {
  AgencyAccountContextDto,
  AgencySiteBillingDetailsDto,
  UwV2ExposedAgencySubscriptionsSitesBillingDetailData,
} from '../../userway/sdk/UserWayApiClient';

@Injectable({
  providedIn: 'root',
})
export class AgencyService extends MCActionsService {
  summaryData: AgencyAccountContextDto;
  loading = new BehaviorSubject(false);
  summaryDataTriggerSubject = new Subject();
  agencyBulkPrepaidSubscription: AgencySubscriptions;
  isAgencyQuotaUsed: boolean = false;

  constructor(
    private agencyServiceApi: AgencyApiService,
    userInfoQuery: UserInfoQuery,
  ) {
    super(userInfoQuery);
    this.initialized$.next(true);
  }

  get currentSubscription() {
    return this.summaryData?.agencySubscriptions.find(
      item =>
        item.status === AgencySubscriptionStatus.ACTIVE ||
        item.status === AgencySubscriptionStatus.NEW ||
        item.status === AgencySubscriptionStatus.DISABLED,
    );
  }

  get agencyMode() {
    return this.userInfo.agencyAccount?.enabled;
  }

  getAgencySummary(userId: string): void {
    this.loading.next(true);
    this.agencyServiceApi.getAgencyAccountSummary(userId).subscribe({
      next: response => {
        this.summaryData = response.payload;
        this.isAgencyQuotaUsed = this.summaryData.quotaTotals.prePaidUsed === this.summaryData.quotaTotals.prePaidTotal;
        this.agencyBulkPrepaidSubscription = this.summaryData.agencySubscriptions.find(
          sub =>
            (sub.status === AgencySubscriptionStatus.ACTIVE || sub.status === AgencySubscriptionStatus.NEW) &&
            sub.type === AgencySubscriptionType.BULK_PREPAID,
        );
      },
      complete: () => {
        this.loading.next(false);
        this.summaryDataTriggerSubject.next(true);
      },
    });
  }

  getAgencySiteBillingDetails(agencySubscriptionId: number, siteId: number): Observable<AgencySiteBillingDetailsDto> {
    if (!siteId || !agencySubscriptionId) {
      return;
    }
    return this.agencyServiceApi.getAgencySiteBillingDetails(agencySubscriptionId, siteId).pipe(
      map((response: UwV2ExposedAgencySubscriptionsSitesBillingDetailData) => {
        return response.payload;
      }),
      catchError(response => {
        response.error?.errors?.forEach((error, index) => {
          console.error(`Error ${index + 1}: Message: ${error.errorMessage}, Type: ${error.errorType}`);
        });
        throw response;
      }),
    );
  }

  clearAgencyData() {
    this.summaryData = null;
  }

  mapProducts(productsResponseData: ProductsAgency): IProductsAgencyConfig[] {
    const products = [];
    for (const key in productsResponseData) {
      if (!productsResponseData.hasOwnProperty(key)) {
        continue;
      }

      products.push({
        key,
        enabled: productsResponseData[key].isEnabled,
        cost: productsResponseData[key].totals?.totalUsd,
        paidAsAgency: productsResponseData[key].agencyState === AgencyState.PAID && productsResponseData[key].isEnabled,
        discount: productsResponseData[key].totals?.discountUsd,
        agencyFree: productsResponseData[key].agencyState === AgencyState.FREE && productsResponseData[key].isEnabled,
        startsAt: productsResponseData[key].isEnabled && productsResponseData[key].startsAt,
        expiresAt: productsResponseData[key].isEnabled && productsResponseData[key].expiresAt,
        disabled: !productsResponseData[key].agencyState && productsResponseData[key].isEnabled,
      });
    }

    return products;
  }
}
